/** One resolved value and one editing surface per screening fact. */
import React from 'react';
import { Check, Pencil, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import type { ChecklistItem } from '../../../config/documentChecklists';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { ExtractedClaim } from '../../../types/siteContext';
import type { SiteDocument } from '../../../types/siteRag';
import type { ResolveReportFieldsResult } from '../../../lib/coverage/resolveReportFields';
import { extractedFieldsForRequirement, type ExtractedFieldRow, type FieldReviewDecision } from '../../../lib/coverage/extractedFieldReview';
import { getAlignedFieldPaths } from '../../../lib/coverage/requirementFieldAlignment';
import { resolveAutoFillOverlayField } from '../../../lib/coverage/autoFillOverlay';
import { useStore } from '../../../store';
import { RequirementInlineAuthorForm } from './RequirementInlineAuthorForm';

interface ScreeningFactsProps {
  item: ChecklistItem;
  authoringItem?: ChecklistItem;
  fields: ResolveReportFieldsResult;
  sed?: SiteEvidenceDocument;
  siteId?: string;
  documents?: readonly SiteDocument[];
  existingClaim?: ExtractedClaim;
  onSaved?: (result: { claimId: string; requirementId: string }) => void;
  onReloadSed?: () => void;
  onOpenAuthor: () => void;
  onOpenSiteIntent?: (fieldKey?: string) => void;
  onViewVariables?: (path?: string) => void;
}

export function ScreeningFacts({ item, authoringItem = item, fields, sed, siteId, documents, existingClaim,
  onSaved, onReloadSed, onOpenAuthor, onOpenSiteIntent, onViewVariables }: ScreeningFactsProps) {
  const [filter, setFilter] = React.useState<'all' | 'unknown' | 'known'>('all');
  const [query, setQuery] = React.useState('');
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [busyPath, setBusyPath] = React.useState<string | null>(null);
  React.useEffect(() => { setFilter('all'); setQuery(''); setEditingKey(null); }, [item.id]);
  const extracted = React.useMemo(() => extractedFieldsForRequirement(sed, item.id, item), [sed, item]);
  const related = fields.related;
  const all = [...fields.primary, ...related];
  const known = all.filter(field => !field.isEmpty).length;
  const normalizedQuery = query.trim().toLowerCase();
  const visible = (field: (typeof all)[number]) => {
    // Filtering never unmounts an active editor or discards its unsaved value.
    if ('key' in field && editingKey === field.key) return true;
    const matchesFilter = filter === 'all' || (filter === 'unknown' ? field.isEmpty : !field.isEmpty);
    const matchesQuery = !normalizedQuery || [field.label, field.displayValue,
      'key' in field ? field.key : field.path].some(text => text.toLowerCase().includes(normalizedQuery));
    return matchesFilter && matchesQuery;
  };
  const controls = 'rounded-sm px-2 py-1 text-[11px] text-text-muted hover:text-primary focus-visible:outline focus-visible:outline-primary disabled:opacity-50';

  const reviewFor = (key: string): ExtractedFieldRow | undefined => {
    const paths = getAlignedFieldPaths(item.id, key);
    return extracted.find(row => row.fieldKey === key || paths.some(path => path === row.fieldPath));
  };
  const documentName = (row: ExtractedFieldRow) => {
    const docId = resolveAutoFillOverlayField(sed, item.id, row.fieldKey)?.docId;
    return documents?.find(doc => doc.id === docId)?.fileName;
  };
  const decide = async (row: ExtractedFieldRow, decision: FieldReviewDecision | null) => {
    if (!siteId || busyPath) return;
    setBusyPath(row.fieldPath);
    try {
      const result = await useStore.getState().reviewExtractedField(siteId, row.sectionId, row.fieldKey, decision);
      if (!result.ok) { toast.error(result.reason ?? 'The check could not be saved.'); return; }
      toast.success(decision === null ? 'Check undone.' : decision === 'rejected' ? 'Incorrect value removed.' : 'Fact checked against the document.');
      onReloadSed?.();
    } catch (error) { toast.error((error as Error)?.message ?? 'The check could not be saved.'); }
    finally { setBusyPath(null); }
  };

  return (
    <section className="space-y-3" data-testid="coverage-screening-facts">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-text-muted" data-testid="screening-facts-count">{known} known, {all.length - known} unknown</p>
        <div className="flex gap-1" role="group" aria-label="Filter facts">
          {(['all', 'unknown', 'known'] as const).map(value => <button key={value} type="button"
            aria-pressed={filter === value} onClick={() => setFilter(value)}
            className={`rounded-sm px-2 py-1 text-[11px] focus-visible:outline focus-visible:outline-primary ${filter === value ? 'bg-primary/10 text-primary' : 'text-text-muted hover:text-text-primary'}`}>
            {value === 'all' ? 'All' : value === 'unknown' ? 'Unknown' : 'Known'}
          </button>)}
        </div>
      </div>
      <input type="search" aria-label="Find a fact" placeholder="Find a fact in this topic"
        value={query} onChange={event => setQuery(event.target.value)}
        className="h-9 w-full rounded-md border border-border-subtle bg-surface-1 px-3 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
      <ul className="divide-y divide-border-subtle">
        {fields.primary.filter(field => visible(field)).map(field => {
          const candidate = reviewFor(field.key);
          const sourceIsDocument = ['doc', 'doc-override', 'provider+doc'].includes(field.provenance);
          const review = candidate && ((sourceIsDocument && Object.is(candidate.value, field.value))
            || (field.isEmpty && candidate.review?.decision === 'rejected')) ? candidate : undefined;
          const isEditing = editingKey === field.key;
          const canEditHere = Boolean(siteId && item.kind !== 'site-intent' && item.kind !== 'report-setup');
          const value = field.value;
          const seed = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
            ? { [field.key]: value } : undefined;
          return <li key={field.key} className="py-2.5" data-testid={`coverage-report-field-${field.key}`}>
            {isEditing ? <RequirementInlineAuthorForm key={`${item.id}:${field.key}`} singleField
              siteId={siteId!} item={authoringItem} existingClaim={existingClaim} fieldKeys={[field.key]} initialValues={seed}
              onCancel={() => setEditingKey(null)} onSaved={result => { setEditingKey(null); onSaved?.(result); onReloadSed?.(); }} /> : <>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <span className="block text-xs text-text-secondary">{field.label}</span>
                  <span className={`block text-[12px] break-words ${field.isEmpty ? 'text-text-muted' : 'text-text-primary font-medium'}`}>
                    {field.isEmpty ? /^Unknown/.test(field.displayValue) ? field.displayValue : 'Unknown' : field.displayValue}
                  </span>
                </div>
                <button type="button" className={controls} data-testid={`coverage-report-field-edit-${field.key}`}
                  aria-label={`${field.isEmpty ? 'Add' : 'Edit'} ${field.label}`}
                  onClick={() => canEditHere ? setEditingKey(field.key) : item.kind === 'site-intent' ? onOpenSiteIntent?.(field.key) : onOpenAuthor()}>
                  <Pencil className="inline h-3 w-3 mr-1" aria-hidden />{field.isEmpty ? 'Add' : 'Edit'}
                </button>
              </div>
              {(!field.isEmpty || review) && <details className="mt-1 text-[10px] text-text-muted" data-testid={`screening-fact-source-${field.key}`}>
                <summary className="cursor-pointer w-fit rounded-sm focus-visible:outline focus-visible:outline-primary">
                  {field.provenance === 'authored' ? 'Entered by user' : field.provenance === 'missing' ? 'Document check' : field.provenance === 'doc' ? 'Document' : 'Site evidence'}
                  {review?.review?.decision === 'accepted' && <span className="ml-1 text-primary">Checked</span>}
                </summary>
                <div className="mt-1.5 space-y-1 border-l border-border-subtle pl-2">
                  {field.sourceLabel && <p className="break-words">{field.sourceLabel}</p>}
                  {field.authoredBy && <p>Entered by {field.authoredBy}</p>}
                  {review && documentName(review) && <p className="break-words">{documentName(review)}</p>}
                  {review && siteId && <div className="flex flex-wrap gap-1">
                    {review.review ? <button type="button" className={controls} disabled={Boolean(busyPath)}
                      data-testid={`extracted-field-undo-${review.fieldKey}`} onClick={() => { void decide(review, null); }}>
                      <RotateCcw className="inline h-3 w-3 mr-1" aria-hidden />Undo check
                    </button> : <>
                      <button type="button" className={controls} disabled={Boolean(busyPath)}
                        data-testid={`extracted-field-accept-${review.fieldKey}`} onClick={() => { void decide(review, 'accepted'); }}>
                        <Check className="inline h-3 w-3 mr-1" aria-hidden />Confirm against document
                      </button>
                      <button type="button" className={`${controls} hover:text-danger`} disabled={Boolean(busyPath)}
                        data-testid={`extracted-field-reject-${review.fieldKey}`} onClick={() => { void decide(review, 'rejected'); }}>Remove incorrect value</button>
                    </>}
                  </div>}
                </div>
              </details>}
            </>}
          </li>;
        })}
        {related.filter(field => visible(field)).map(field => <li key={field.path} className="py-2.5" data-testid={`coverage-panel-field-${field.path}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <span className="block text-xs text-text-secondary">{field.label}</span>
              <span className={`block text-[12px] break-words ${field.isEmpty ? 'text-text-muted' : 'text-text-primary font-medium'}`}>{field.isEmpty ? 'Unknown' : field.displayValue}</span>
            </div>
            {onViewVariables && <button type="button" className={controls} onClick={() => onViewVariables(field.path)} aria-label={`View source for ${field.label}`}>View source</button>}
          </div>
          {field.sourceLabel && <details className="mt-1 text-[10px] text-text-muted"><summary className="cursor-pointer w-fit">Site evidence</summary><p className="mt-1 break-words">{field.sourceLabel}</p></details>}
        </li>)}
      </ul>
      {all.filter(field => visible(field)).length === 0 && <p className="text-[11px] text-text-muted py-3">{normalizedQuery ? 'No facts match this search.' : filter === 'known' ? 'No known facts yet. Add what you know or upload a document.' : 'No unknown facts in this topic.'}</p>}
    </section>
  );
}
